/**
 * TagTogglePicker — reusable, importable tag-toggle section used by the
 * profile modal (Regional Expertise / Disciplines / Product Expertise) and
 * the professionals create/edit form.
 *
 * Two item kinds, passed via `items`:
 *   • string           — a SINGLE tag toggle: one chip, click toggles it.
 *   • TagToggleGroup   — a GROUP toggle (e.g. a super region): clicking the
 *     chip expands its children as a 2-per-row grid — inline directly under
 *     the group's row when `inModal` (the picker already lives inside a
 *     modal, e.g. the profile dialog), otherwise in a small Dialog. The
 *     expanded panel's "Toggle all" selects every leaf child (filling the
 *     group chip) or clears them again, and auto-collapses the panel either
 *     way. Children may themselves be groups (e.g. European Union / USA
 *     inside Europe / North America): a nested group renders as a group
 *     chip inside the parent's grid and ALWAYS opens the Dialog to reveal
 *     its own regions — nesting a second inline grid would be unreadable.
 *
 * Search: the magnifying glass in the header reveals a filter input. While a
 * query is present the list is reordered (nothing is hidden): alphabetical
 * overall, with the strongest text matches first (label starts-with beats
 * contains; for groups the best child match counts too).
 *
 * Selected values that are not present in `items` (legacy/stale tags) are
 * appended as plain single toggles so they can still be switched off.
 */
import React, { Fragment, useMemo, useState } from "react";
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, TextField, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const isGroup = item => typeof item !== "string";

/** 2 = label starts with query, 1 = label contains query, 0 = no match. */
function matchScore(label, query) {
  if (!query) return 0;
  const l = label.toLowerCase();
  if (l.startsWith(query)) return 2;
  if (l.includes(query)) return 1;
  return 0;
}

/** Groups score as the best of their own label and any descendant label. */
function itemScore(item, query) {
  if (!isGroup(item)) return matchScore(item, query);
  return Math.max(matchScore(item.label, query), ...item.children.map(c => itemScore(c, query)));
}
const itemLabel = item => isGroup(item) ? item.label : item;

/** All selectable (leaf) tag values under a group, depth-first. */
function leafValues(group) {
  const out = [];
  for (const child of group.children) {
    if (isGroup(child)) out.push(...leafValues(child));else out.push(child);
  }
  return out;
}

/** Every group in the tree, keyed by label (supers and nested alike). */
function collectGroups(items, into = new Map()) {
  for (const item of items) {
    if (isGroup(item)) {
      into.set(item.label, item);
      collectGroups(item.children, into);
    }
  }
  return into;
}
export default function TagTogglePicker({
  label,
  items,
  value,
  onChange,
  color,
  colorDark,
  inModal = false,
  searchable = true
}) {
  const dark = colorDark ?? color;
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  /** Labels of groups currently expanded (inline mode). */
  const [expanded, setExpanded] = useState(new Set());
  /** Group open in the Dialog (non-modal mode). */
  const [dialogGroup, setDialogGroup] = useState(null);
  const groupsByLabel = useMemo(() => collectGroups(items), [items]);

  /**
   * Legacy stored preferences (RegionPicker's value shape) may contain a
   * super-region marker like "North America" meaning "all of its
   * children". Group labels aren't toggleable values here, so materialize
   * markers into their children — for display and for every emitted
   * change — and stale markers normalize away on the first edit.
   */
  const effectiveValue = useMemo(() => {
    const out = [];
    const seen = new Set();
    for (const v of value) {
      const group = groupsByLabel.get(v);
      for (const tag of group ? leafValues(group) : [v]) {
        if (!seen.has(tag)) {
          seen.add(tag);
          out.push(tag);
        }
      }
    }
    return out;
  }, [value, groupsByLabel]);
  const selected = useMemo(() => new Set(effectiveValue), [effectiveValue]);

  // Selected values not covered by `items` — keep them visible/toggleable.
  const strayValues = useMemo(() => {
    const known = new Set();
    for (const item of items) {
      if (isGroup(item)) {
        leafValues(item).forEach(c => known.add(c));
      } else {
        known.add(item);
      }
    }
    return effectiveValue.filter(v => !known.has(v));
  }, [items, effectiveValue]);
  const q = query.trim().toLowerCase();

  // Reorder (never hide): score desc, then alphabetical.
  const orderedItems = useMemo(() => {
    const all = [...items, ...strayValues];
    if (!q) return all;
    return [...all].sort((a, b) => {
      const diff = itemScore(b, q) - itemScore(a, q);
      if (diff !== 0) return diff;
      return itemLabel(a).localeCompare(itemLabel(b));
    });
  }, [items, strayValues, q]);
  const orderedChildren = group => {
    if (!q) return group.children;
    return [...group.children].sort((a, b) => {
      const diff = itemScore(b, q) - itemScore(a, q);
      if (diff !== 0) return diff;
      return itemLabel(a).localeCompare(itemLabel(b));
    });
  };
  const toggleSingle = tag => {
    onChange(selected.has(tag) ? effectiveValue.filter(v => v !== tag) : [...effectiveValue, tag]);
  };
  const collapseGroup = group => {
    setExpanded(prev => {
      if (!prev.has(group.label)) return prev;
      const next = new Set(prev);
      next.delete(group.label);
      return next;
    });
    setDialogGroup(cur => cur?.label === group.label ? null : cur);
  };

  /**
   * "Toggle all" in the expanded panel: all leaf children selected → clear
   * them, else select all — the only way to fill the group chip in one
   * action. The panel auto-collapses afterwards either way.
   */
  const toggleGroupAll = group => {
    const leaves = leafValues(group);
    const allOn = leaves.every(c => selected.has(c));
    if (allOn) {
      const drop = new Set(leaves);
      onChange(effectiveValue.filter(v => !drop.has(v)));
    } else {
      const next = new Set(effectiveValue);
      leaves.forEach(c => next.add(c));
      onChange(Array.from(next));
    }
    collapseGroup(group);
  };

  /**
   * Nested groups (EU inside Europe, USA inside North America) always open
   * the Dialog — a second inline grid inside the parent's grid would be
   * unreadable. Top-level groups expand inline when `inModal`.
   */
  const openGroup = (group, nested = false) => {
    if (inModal && !nested) {
      setExpanded(prev => {
        const next = new Set(prev);
        if (next.has(group.label)) next.delete(group.label);else next.add(group.label);
        return next;
      });
    } else {
      setDialogGroup(cur => cur?.label === group.label ? null : group);
    }
  };
  const chipSx = active => ({
    borderColor: color,
    color: active ? "#fff" : dark,
    bgcolor: active ? color : "transparent",
    fontWeight: 600,
    "&:hover": {
      bgcolor: active ? dark : alpha(color, 0.1)
    }
  });
  const renderChildGrid = group => /*#__PURE__*/_jsxs(Box, {
    sx: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 1,
      p: 1.5,
      mt: 0.5,
      borderRadius: 1,
      border: `1px dashed ${alpha(color, 0.4)}`,
      backgroundColor: alpha(color, 0.04)
    },
    children: [/*#__PURE__*/_jsx(Box, {
      sx: {
        gridColumn: "1 / -1",
        textAlign: "right"
      },
      children: /*#__PURE__*/_jsx(Button, {
        size: "small",
        onClick: () => toggleGroupAll(group),
        sx: {
          color: dark,
          fontWeight: 600
        },
        children: leafValues(group).every(c => selected.has(c)) ? "Clear all" : "Toggle all"
      })
    }), orderedChildren(group).map(child => {
      if (isGroup(child)) {
        return /*#__PURE__*/_jsx(Fragment, {
          children: renderGroupChip(child, true)
        }, child.label);
      }
      const active = selected.has(child);
      return /*#__PURE__*/_jsx(Chip, {
        label: child,
        size: "small",
        variant: active ? "filled" : "outlined",
        onClick: () => toggleSingle(child),
        sx: {
          ...chipSx(active),
          justifyContent: "flex-start",
          width: "100%"
        }
      }, child);
    })]
  });
  const renderGroupChip = (group, nested = false) => {
    const leaves = leafValues(group);
    const count = leaves.filter(c => selected.has(c)).length;
    const allOn = count === leaves.length;
    const isOpen = inModal && !nested ? expanded.has(group.label) : dialogGroup?.label === group.label;
    const text = `${group.label}${count ? ` ${count}/${leaves.length}` : ""}`;
    return /*#__PURE__*/_jsx(Tooltip, {
      title: isOpen ? `Collapse ${group.label} options` : `Expand ${group.label} options`,
      children: /*#__PURE__*/_jsx(Chip, {
        label: /*#__PURE__*/_jsxs(Box, {
          component: "span",
          sx: {
            display: "inline-flex",
            alignItems: "center",
            gap: 0.25
          },
          children: [text, isOpen ? /*#__PURE__*/_jsx(ExpandLessIcon, {
            fontSize: "small"
          }) : /*#__PURE__*/_jsx(ExpandMoreIcon, {
            fontSize: "small"
          })]
        }),
        size: "small",
        variant: allOn ? "filled" : "outlined",
        onClick: () => openGroup(group, nested),
        "aria-expanded": isOpen,
        "aria-haspopup": inModal && !nested ? undefined : "dialog",
        "aria-label": `${group.label}: ${isOpen ? "collapse" : "expand"} options`,
        sx: {
          ...chipSx(allOn),
          ...(nested ? {
            justifyContent: "flex-start",
            width: "100%"
          } : {})
        }
      })
    });
  };
  return /*#__PURE__*/_jsxs(Box, {
    children: [/*#__PURE__*/_jsxs(Box, {
      sx: {
        display: "flex",
        alignItems: "center",
        gap: 0.5
      },
      children: [/*#__PURE__*/_jsx(Typography, {
        variant: "subtitle2",
        sx: {
          fontWeight: 600,
          color
        },
        children: label
      }), searchable && /*#__PURE__*/_jsx(Tooltip, {
        title: `Search ${label.toLowerCase()}`,
        children: /*#__PURE__*/_jsx(IconButton, {
          size: "small",
          "aria-label": `Search ${label}`,
          onClick: () => {
            setSearchOpen(v => {
              if (v) setQuery("");
              return !v;
            });
          },
          sx: {
            color
          },
          children: /*#__PURE__*/_jsx(SearchIcon, {
            fontSize: "small"
          })
        })
      })]
    }), searchable && searchOpen && /*#__PURE__*/_jsx(TextField, {
      size: "small",
      fullWidth: true,
      autoFocus: true,
      placeholder: `Filter ${label.toLowerCase()}…`,
      value: query,
      onChange: e => setQuery(e.target.value),
      sx: {
        mb: 1,
        maxWidth: 340
      },
      InputProps: {
        startAdornment: /*#__PURE__*/_jsx(InputAdornment, {
          position: "start",
          children: /*#__PURE__*/_jsx(SearchIcon, {
            fontSize: "small",
            sx: {
              color
            }
          })
        }),
        endAdornment: /*#__PURE__*/_jsx(InputAdornment, {
          position: "end",
          children: /*#__PURE__*/_jsx(IconButton, {
            size: "small",
            "aria-label": "Close search",
            onClick: () => {
              setQuery("");
              setSearchOpen(false);
            },
            children: /*#__PURE__*/_jsx(CloseIcon, {
              fontSize: "small"
            })
          })
        })
      }
    }), /*#__PURE__*/_jsx(Box, {
      sx: {
        display: "flex",
        flexWrap: "wrap",
        gap: 0.75,
        mt: 0.5
      },
      children: orderedItems.map(item => {
        if (isGroup(item)) {
          const isOpen = inModal && expanded.has(item.label);
          return /*#__PURE__*/_jsxs(Fragment, {
            children: [renderGroupChip(item), isOpen && /*#__PURE__*/_jsx(Box, {
              sx: {
                flexBasis: "100%"
              },
              children: renderChildGrid(item)
            })]
          }, item.label);
        }
        const active = selected.has(item);
        return /*#__PURE__*/_jsx(Chip, {
          label: item,
          size: "small",
          variant: active ? "filled" : "outlined",
          onClick: () => toggleSingle(item),
          sx: chipSx(active)
        }, item);
      })
    }), /*#__PURE__*/_jsx(Dialog, {
      open: !!dialogGroup,
      onClose: () => setDialogGroup(null),
      fullWidth: true,
      maxWidth: "xs",
      children: dialogGroup && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsxs(DialogTitle, {
          component: "div",
          sx: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          },
          children: [/*#__PURE__*/_jsx(Typography, {
            variant: "h6",
            component: "h2",
            sx: {
              color: dark
            },
            children: dialogGroup.label
          }), /*#__PURE__*/_jsx(IconButton, {
            size: "small",
            "aria-label": "Close",
            onClick: () => setDialogGroup(null),
            children: /*#__PURE__*/_jsx(CloseIcon, {
              fontSize: "small"
            })
          })]
        }), /*#__PURE__*/_jsx(DialogContent, {
          dividers: true,
          children: renderChildGrid(dialogGroup)
        }), /*#__PURE__*/_jsx(DialogActions, {
          children: /*#__PURE__*/_jsx(Button, {
            onClick: () => setDialogGroup(null),
            children: "Done"
          })
        })]
      })
    })]
  });
}